import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DynamicForm, SelectOption } from '../../../components/form-builder/form-builder.types';
import { FormBuilderComponent } from '../../../components/form-builder/form-builder.component';
import { MultiFormAccordionService } from '../../../components/multi-form-accordion/multi-form-accordion.service';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { ButtonModule } from 'primeng/button';
import { PaaRepository } from '../../../store/paa-state/paa-state.repository';
import { PaaService } from '../paa.service';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { getPaaNewPlanFormObject } from './utils/paa-create-form-object';
import { PaaMilestonesService } from '../paa-milestones.service';
import { PaaWorkflowBlocksService } from '../paa-workflow-blocks.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { BlockBuilderComponent } from '../../../components/block-builder/block-builder.component';
import { paaPreDraftSchema } from '@/validations/paa/paa-predraft.validation';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { StatusComponent } from '../../../components/shared/status/status.component';
import { BehaviorSubject } from 'rxjs';
import { InformativeStatusComponent } from '../../../components/shared/informative-status/informative-status.component';
import { CommonModule } from '@angular/common';
import { getPaaNoContractingAuthorityInformativeStatus } from './utils/paa-no-ac.informative';
import { InformativeStatus } from '@/components/shared/informative-status/informative-status.types';
import { SpinnerComponent } from '../../../components/shared/spinner/spinner.component';
import { paaMilestonesStoreFactory } from '@/store/paa-state/milestones-state/milestones.store';
import { AcYearAvailability } from '@/types';

@Component({
  selector: 'sm-paa-new-plan',
  imports: [
    CommonModule,
    FormBuilderComponent,
    PageHeaderComponent,
    ButtonModule,
    BlockBuilderComponent,
    StatusComponent,
    InformativeStatusComponent,
    SpinnerComponent,
  ],
  templateUrl: './new-plan.component.html',
  styleUrl: './new-plan.component.scss',
})
export class PaaNewPlanComponent implements OnInit, OnDestroy {
  //services
  paaService = inject(PaaService);
  paaMilestonesService = inject(PaaMilestonesService);
  paaWorkflowBlocksService = inject(PaaWorkflowBlocksService);
  paaRepository = inject(PaaRepository);
  multiFormAccordionService = inject(MultiFormAccordionService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  messageService = inject(MessageService);
  router = inject(Router);

  //breadcrumb
  breadcrumb = [
    {
      label: 'paa.title',
      routerLink: ['/paa-history'],
    },
    {
      label: 'paa.variants.new',
      routerLink: ['paa-new-plan'],
    },
  ];

  loading$ = new BehaviorSubject<boolean>(true);
  warning?: InformativeStatus;
  acOptions: SelectOption[] = [];
  acAvailability: AcYearAvailability[] = [];
  allowedYearsByAc = new Map<number, number[]>();
  yearDisabled$ = new BehaviorSubject<boolean>(true);
  currentAllowedYears: number[] = [];
  yearBounds: { minDate?: Date; maxDate?: Date } = {};
  formObject: DynamicForm = getPaaNewPlanFormObject({
    store: this.paaRepository,
    acOptions: this.acOptions,
  });
  controlBlocks: DynamicBlocks = this.paaWorkflowBlocksService.getPreDraftPaaBlocks({
    save: () => this.addPaa(),
    rest: () => this.reset(),
  });

  constructor() {
    this.multiFormAccordionService.formFactory = (id: string) => paaMilestonesStoreFactory(id);
    this.multiFormAccordionService.init();
    this.buildForm();
  }

  ngOnInit() {
    this.loaddData();
  }

  ngOnDestroy() {
    this.multiFormAccordionService.reset(0);
    this.paaRepository.reset();
  }

  loaddData() {
    this.paaService.getAcWithNoPaa().subscribe({
      next: data => {
        this.acAvailability = data;
        this.allowedYearsByAc = new Map(data.map(ac => [ac.acId, ac.allowedYears || []]));
        this.acOptions = data.map(ac => {
          return { name: ac.acName, code: ac.acId };
        });
        if (this.acOptions.length === 1) {
          this.paaRepository.setNested('dto', 'acId', this.acOptions[0].code as number);
          this.paaRepository.set('acsSelected', this.acOptions[0]);
          this.applyAllowedYears(this.acOptions[0].code as number);
        } else {
          this.applyAllowedYears(undefined);
        }
        this.buildForm();
        this.loading$.next(false);
      },
      error: err => {
        this.warning = getPaaNoContractingAuthorityInformativeStatus({
          subtitle: err.error.message,
          goToPaaCurrentPlans: () => this.router.navigate(['/paa-current-plans']),
        });
        this.loading$.next(false);
      },
    });
    this.paaMilestonesService.initialize();
  }

  addPaa() {
    const year = this.paaRepository.getNested('dto', 'annee');
    const milestoneValidation = this.paaMilestonesService.validateAllMilestoneDtos('draft', year ? new Date(Number(year), 0, 1) : undefined);
    const payload = this.paaRepository.getCreatePayload(this.paaMilestonesService.getAllMilestoneDtos());
    const result = paaPreDraftSchema.safeParse(payload.data);

    if (result.success && milestoneValidation) {
      this.loading$.next(true);
      this.paaService.addPaaPerAc(payload.formData).subscribe({
        next: () => {
          this.multiFormAccordionService.reset(0);
          this.paaRepository.reset();
          this.router.navigate(['/paa-current-plans']);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Le paa a été ajouté avec succès',
          });
          this.loading$.next(false);
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || "Une erreur est survenue lors de l'ajout du paa",
          });
          this.loading$.next(false);
        },
      });
    } else if (result.error) {
      this.paaRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  reset() {
    this.paaRepository.reset();
    this.multiFormAccordionService.reset();
    if (this.acOptions.length === 1) {
      this.paaRepository.setNested('dto', 'acId', this.acOptions[0].code as number);
      this.paaRepository.set('acsSelected', this.acOptions[0]);
      this.applyAllowedYears(this.acOptions[0].code as number);
    } else {
      this.applyAllowedYears(undefined);
    }
    this.buildForm();
  }

  private buildForm() {
    this.formObject = getPaaNewPlanFormObject({
      store: this.paaRepository,
      acOptions: this.acOptions,
      yearConfig: {
        minDate: this.yearBounds.minDate,
        maxDate: this.yearBounds.maxDate,
        allowedYears: this.currentAllowedYears,
        isDisabled: this.yearDisabled$,
      },
      onAcChange: acId => this.handleAcChange(acId),
    });
  }

  private handleAcChange(acId?: number) {
    this.applyAllowedYears(acId);
    this.buildForm();
  }

  private applyAllowedYears(acId?: number) {
    if (!acId) {
      this.currentAllowedYears = [];
      this.yearBounds = {};
      this.yearDisabled$.next(true);
      this.paaRepository.setNested('dto', 'annee', undefined);
      return;
    }
    const years = this.allowedYearsByAc.get(acId) || [];
    this.currentAllowedYears = years;
    if (!years.length) {
      this.yearDisabled$.next(true);
      this.paaRepository.setNested('dto', 'annee', undefined);
      this.yearBounds = {};
      return;
    }
    this.yearDisabled$.next(false);
    if (years.length === 1) {
      this.paaRepository.setNested('dto', 'annee', years[0].toString());
    } else {
      const currentYear = Number(this.paaRepository.getNested('dto', 'annee'));
      if (!years.includes(currentYear)) {
        this.paaRepository.setNested('dto', 'annee', years[0].toString());
      }
    }
    this.yearBounds = {
      minDate: new Date(Math.min(...years), 0, 1),
      maxDate: new Date(Math.max(...years), 11, 31),
    };
  }
}
